import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Alert {
  severity: 'info' | 'warning' | 'critical';
  message: string;
}

interface AlertsPanelProps {
  alerts: Alert[];
  className?: string;
}

const severityConfig = {
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    badgeVariant: 'secondary' as const,
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    badgeVariant: 'outline' as const,
  },
  critical: {
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    badgeVariant: 'destructive' as const,
  },
};

export function AlertsPanel({ alerts, className }: AlertsPanelProps) {
  if (alerts.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Platform Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No active alerts</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Platform Alerts</CardTitle>
          <Badge variant="secondary">{alerts.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {alerts.map((alert, index) => {
          const config = severityConfig[alert.severity];
          const Icon = config.icon;
          return (
            <div
              key={index}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3',
                config.bgColor,
                config.borderColor
              )}
            >
              <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', config.textColor)} />
              <div className="flex-1">
                <p className={cn('text-sm font-medium', config.textColor)}>{alert.message}</p>
              </div>
              <Badge variant={config.badgeVariant} className="shrink-0 capitalize">
                {alert.severity}
              </Badge>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
